/** Client-side password guidance (NIST 800-63B: length over composition).
 *
 *  The SERVER policy (tenancy/passwords.py — min 12, common-password
 *  denylist, no personal info) is the gate; the zxcvbn score here is
 *  guidance only and NEVER blocks submission — a low score with 12+
 *  characters can still be submitted, a high score under 12 cannot.
 */

import { Options, ZxcvbnFactory } from '@zxcvbn-ts/core'
import { adjacencyGraphs, dictionary } from '@zxcvbn-ts/language-common'

const zxcvbn = new ZxcvbnFactory(new Options({ dictionary, graphs: adjacencyGraphs }))

export const PASSWORD_MIN_LENGTH = 12

export type Strength = {
  /** zxcvbn score 0-4. */
  score: 0 | 1 | 2 | 3 | 4
  label: string
  /** Length-nudging guidance shown under the meter. */
  hint: string
  /** Hard client-side gate (mirrors the server's minimum). */
  meetsMinimum: boolean
}

const LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'] as const

export function assessPassword(password: string): Strength {
  const meetsMinimum = password.length >= PASSWORD_MIN_LENGTH
  if (!password) {
    return { score: 0, label: '', hint: `At least ${PASSWORD_MIN_LENGTH} characters — length matters most.`, meetsMinimum }
  }
  const score = zxcvbn.check(password).score
  let hint: string
  if (!meetsMinimum) {
    hint = `Add ${PASSWORD_MIN_LENGTH - password.length} more character${PASSWORD_MIN_LENGTH - password.length === 1 ? '' : 's'} — ${PASSWORD_MIN_LENGTH} is the minimum.`
  } else if (score <= 1) {
    hint = 'Easy to guess — a longer phrase of unrelated words is much stronger.'
  } else if (score === 2) {
    hint = 'Okay — more characters would make it stronger.'
  } else {
    hint = 'Good password. Common passwords and your name/email are rejected on save.'
  }
  return { score, label: LABELS[score], hint, meetsMinimum }
}
